import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Counter from "../components/Counter";
import Seo from "../components/Seo";
import { STATS } from "../data/content";

const STANDARD = [
  { k: "01", title: "Outcomes over hours", desc: "We don't sell time on a clock. We're measured by deals moved, hours returned, and deadlines never missed." },
  { k: "02", title: "A human owns every call", desc: "AI carries the volume; a trained operator owns the judgment. Nothing important gets automated into the void." },
  { k: "03", title: "Your systems, made sharper", desc: "We plug into the stack you already run and leave it stronger, not a rip-and-replace, a genuine upgrade." },
  { k: "04", title: "Invisible when it works", desc: "The best right hand is the one you stop thinking about. When ops disappear into the background, we've done our job." },
];

const ETHOS = [
  { title: "Human in the loop", desc: "AI moves the volume; trained operators own the judgment. Nothing important gets automated into the void." },
  { title: "Built for closings", desc: "Every workflow we run is measured against one thing: does it help you close the next deal faster." },
  { title: "Borderless by design", desc: "US brokerages and global teams run on Quexify, with coverage that follows the sun." },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 md:pt-44">
      <Seo
        title="Our Story | Quexify — The Right-Hand Standard"
        description="Quexify was built to change the ratio between selling and everything around it. Our mission, our ethos, and the standard behind every AI-augmented right hand we run."
        path="/about"
      />

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <h1 className="font-display text-5xl md:text-8xl tracking-tighter font-medium leading-[0.9] max-w-4xl">
            We run the ops so agents can be agents.
          </h1>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12 grid md:grid-cols-12 gap-8">
            <p className="md:col-span-7 text-xl md:text-2xl text-neutral-600 leading-relaxed tracking-tight">
              Real estate runs on relationships, but agents drown in everything around the deal: transactions, marketing, social, automations, admin, follow-ups and networking. Quexify was built to change that ratio with an AI-augmented right hand that carries all of it, so your best people spend their time where the business actually grows.
            </p>
            <p className="md:col-span-4 md:col-start-9 text-neutral-500 leading-relaxed self-end">
              Headquartered for the US market and operating globally, we plug into your existing stack and become the invisible operations team behind your growth.
            </p>
          </div>
        </Reveal>
      </section>

      {/* MISSION */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <div className="grid md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <p className="text-sm text-neutral-400">Our mission</p>
            <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98]">
              Give every agent a right hand.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-7 md:col-start-6 self-end">
            <p className="text-lg md:text-xl text-neutral-500 leading-relaxed">
              For decades the only way to buy back your time was to hire, train, and manage, or to settle for a cheap assistant who processed tasks and little else. We believed there was a better model: one dedicated operator, fluent in real estate and amplified by AI, accountable for the whole back office and the outcomes it drives. That belief is Quexify, and it's why we exist.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE STANDARD */}
      <section className="bg-[#0a0a0a] text-white" data-testid="standard-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <Reveal className="max-w-2xl mb-16 md:mb-20">
            <p className="text-sm text-neutral-500 mb-8">The Quexify standard</p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98]">
              Four principles we don't bend on.
            </h2>
          </Reveal>
          <div className="border-t border-white/10">
            {STANDARD.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.06}>
                <div className="grid md:grid-cols-12 gap-3 md:gap-10 items-baseline py-8 md:py-10 border-b border-white/10" data-testid={`standard-${i}`}>
                  <div className="md:col-span-1 font-display text-sm text-neutral-500 tabular-nums">{s.k}</div>
                  <h3 className="md:col-span-4 font-display text-2xl md:text-3xl tracking-tight font-medium">{s.title}</h3>
                  <p className="md:col-span-6 md:col-start-7 text-lg text-neutral-400 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ETHOS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <Reveal className="max-w-2xl mb-14 md:mb-20">
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98]">
            How we show up.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-12 md:gap-10">
          {ETHOS.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.1}>
              <div className="border-t border-black/10 pt-8" data-testid={`value-${i}`}>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">{v.title}</h3>
                <p className="mt-4 text-neutral-500 leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-black/5 bg-[#fafafa]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-14 gap-x-8">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="font-display text-5xl md:text-6xl tracking-tighter font-medium tabular-nums"><Counter value={s.num} suffix={s.suffix} /></div>
                <p className="mt-3 text-sm text-neutral-500 max-w-[180px]">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40 text-center">
        <Reveal>
          <h2 className="font-display text-4xl md:text-7xl tracking-tighter font-medium leading-[0.95] max-w-3xl mx-auto">
            Grow the business.<br />We'll carry the rest.
          </h2>
          <Link to="/book-demo" className="mt-12 inline-flex items-center gap-2 rounded-full bg-[#0a0a0a] text-white px-9 py-4 text-lg transition-transform duration-300 hover:-translate-y-0.5" data-testid="about-cta">
            Book a discovery call <ArrowUpRight size={20} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
